import React, { useRef, useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { TimePickerInput } from "../../../common/TimePickerInput";
import CurrentDateTimeButton from "../../../common/CurrentDateTimeButton";
import styles from "./FieldNotesComponents.module.css";

// General Work Description and Weather component
export const GeneralWorkDescriptionAndWeather = ({ callbacks }) => {
  // Handling the collapse button click
  const handleCollapseButtonClick = () => {
    callbacks.collapseGeneralWorkDescriptionWeather(
      !callbacks.generalWorkDescriptionWeatherCollapsed
    );
  };

  const handleWorkDescriptionInputChange = (e, sendDataToBackend = false) => {
    callbacks.updateGeneralWorkDescription(
      e.target.value,
      sendDataToBackend,
      callbacks.emitPatch
    );
  };

  const handleWeatherInputChange = (e, sendDataToBackend = false) => {
    callbacks.updateWeather(
      e.target.value,
      sendDataToBackend,
      callbacks.emitPatch
    );
  };

  return (
    <div className={styles.fieldNotesGeneralInfoWrapper}>
      <div className={styles.labelButtonContainer}>
        <label className={styles.inputLabel}>Work Description</label>
        <button
          className={`${styles.collapseButton} ${
            callbacks.generalWorkDescriptionWeatherCollapsed
              ? styles.collapsed
              : ""
          }`}
          onClick={handleCollapseButtonClick}
        >
          {callbacks.generalWorkDescriptionWeatherCollapsed ? "▼" : "▲"}
        </button>
      </div>
      <div
        className={`${styles.fieldNotesGeneralInfoContainer} ${
          callbacks.generalWorkDescriptionWeatherCollapsed
            ? styles.collapsed
            : ""
        }`}
      >
        <input
          type="text"
          className={`${styles.fieldNotesWorkDescriptionInput} ${
            callbacks.getAutosaveFieldError?.("work_description")
              ? styles.inputError
              : ""
          }`}
          value={callbacks.delData.fieldNotes?.workDescription || ""}
          onChange={handleWorkDescriptionInputChange}
          onBlur={(e) => handleWorkDescriptionInputChange(e, true)}
          placeholder="Work Description"
          disabled={callbacks.delMode === "view"}
        />
        {callbacks.getAutosaveFieldError?.("work_description") && (
          <span className={styles.fieldErrorText}>
            {callbacks.getAutosaveFieldError("work_description")}
          </span>
        )}
        <label className={styles.inputLabel}>Weather</label>
        <input
          type="text"
          className={`${styles.fieldNotesWeatherInput} ${
            callbacks.getAutosaveFieldError?.("weather") ? styles.inputError : ""
          }`}
          value={callbacks.delData.fieldNotes?.weather || ""}
          onChange={handleWeatherInputChange}
          onBlur={(e) => handleWeatherInputChange(e, true)}
          placeholder="Weather"
          disabled={callbacks.delMode === "view"}
        />
        {callbacks.getAutosaveFieldError?.("weather") && (
          <span className={styles.fieldErrorText}>
            {callbacks.getAutosaveFieldError("weather")}
          </span>
        )}
      </div>
    </div>
  );
};

// Field Note Modal Component - uses local state passed from parent
const FieldNoteModal = ({
  isOpen,
  isEditing,
  time,
  notes,
  onTimeChange,
  onNotesChange,
  onClear,
  onSave,
  onClose,
}) => {
  const [timeKey, setTimeKey] = useState(0);
  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3>{isEditing ? "Edit Field Note" : "Add Field Note"}</h3>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>
        <div className={styles.modalBody}>
          {/* Time Field */}
          <div className={styles.modalField}>
            <label className={styles.modalLabel}>Time</label>
            <div className={styles.timePickerContainer}>
              <TimePickerInput
                key={timeKey}
                value={time || ""}
                onChange={onTimeChange}
                className={styles.modalTimeInput}
                interval={1}
              />
              <CurrentDateTimeButton
                onTimeChange={(time) => {
                  onTimeChange(time);
                  setTimeKey((k) => k + 1);
                }}
              />
            </div>
          </div>

          {/* Notes Text Field */}
          <div className={styles.modalField}>
            <label className={styles.modalLabel}>Notes</label>
            <textarea
              className={styles.modalTextarea}
              value={notes || ""}
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder="Enter your notes here..."
              rows={6}
            />
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.clearButton} onClick={onClear}>
            Clear
          </button>
          <div className={styles.modalFooterRight}>
            <button className={styles.cancelButton} onClick={onClose}>
              Close
            </button>
            <button className={styles.saveButton} onClick={onSave}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Field Notes Bubble component (replaces the old activity row/card)
const FieldNotesBubble = ({ activity, callbacks, removeUnitRef, onEdit }) => {
  // compute errors once per render
  const activityErrors =
    callbacks?.delData?.errors?.fieldNotes?.find(
      (e) => e.activityRowId === activity.rowId
    )?.errors || {};

  // Get autosave row errors for this activity
  const autosaveRowError = callbacks.getAutosaveRowError?.(activity.rowId);
  const autosaveFieldErrors = autosaveRowError?.field_errors || {};
  const hasAutosaveError = Object.keys(autosaveFieldErrors).length > 0;

  const [isDeleting, setIsDeleting] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: activity.rowId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Handling the delete button click with animation
  const handleDeleteButtonClick = () => {
    removeUnitRef.current = true;
    setIsDeleting(true);

    // Wait for animation to complete before actually removing
    setTimeout(() => {
      callbacks.removeActivity(activity.rowId, callbacks.emitPatch);
    }, 300); // Match this with CSS animation duration
  };

  // Handling the edit button click
  const handleEditButtonClick = () => {
    onEdit(activity);
  };

  // Check if this bubble has content
  const hasContent = activity.time || activity.notes;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`${styles.fieldNotesBubble} ${
        isDragging ? styles.dragging : ""
      } ${isDeleting ? styles.deleting : ""} ${
        hasContent ? styles.hasContent : styles.emptyBubble
      } ${hasAutosaveError ? styles.bubbleError : ""}`}
    >
      {/* Drag Handle - takes up entire left side */}
      {callbacks.delMode !== "view" && (
        <div className={styles.bubbleDragHandle} {...listeners}>
          <span className={styles.dragHandleIcon}>&#8942;&#8942;</span>
        </div>
      )}

      {/* Bubble Content */}
      <div className={styles.bubbleContent}>
        {/* Top Row: Time, Edit, Delete */}
        <div className={styles.bubbleTopRow}>
          <div className={styles.bubbleTimeSection}>
            <span className={styles.bubbleTimeLabel}>Time:</span>
            <span className={styles.bubbleTimeValue}>
              {activity.time || "--:--"}
            </span>
            {callbacks.delMode !== "view" && (
              <button
                className={styles.bubbleEditButton}
                onClick={handleEditButtonClick}
              >
                Edit
              </button>
            )}
          </div>
          {callbacks.delMode !== "view" && (
            <button
              className={styles.bubbleDeleteButton}
              onClick={handleDeleteButtonClick}
            >
              Delete
            </button>
          )}
        </div>

        {/* Bottom Row: Notes Content */}
        <div className={styles.bubbleNotesSection}>
          {activity.notes ? (
            <p className={styles.bubbleNotesText}>{activity.notes}</p>
          ) : (
            <p className={styles.bubbleNotesPlaceholder}>No notes added</p>
          )}
        </div>

        {/* Autosave Error Display */}
        {hasAutosaveError && (
          <div className={styles.bubbleErrorSection}>
            {Object.entries(autosaveFieldErrors).map(([field, error]) => (
              <span key={field} className={styles.fieldErrorText}>
                {error}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Field Notes activities component
export const FieldNotesActivities = ({
  callbacks,
  removeUnitRef,
  onAddNote,
  onEditNote,
}) => {
  const containerRef = useRef(null);
  // Adding this to keep the container from scrolling when we pop in and out of the unit table.
  const initialRender = useRef(true);
  // Anchor to ensure we scroll after DOM updates
  const bottomAnchorRef = useRef(null);

  // Handling the add note button click - opens modal for new note
  const handleAddNoteButtonClick = () => {
    removeUnitRef.current = false;
    onAddNote();
  };

  // Scroll to bottom anchor on add (not remove), track by activities length
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    if (!removeUnitRef.current && !callbacks.showAll) {
      bottomAnchorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
    // reset the flag so subsequent operations don't block scrolls
    removeUnitRef.current = null;
  }, [callbacks.delData.fieldNotes.activities.length]);

  useEffect(() => {
    return () => {
      initialRender.current = true;
    };
  }, []);

  // DND context for the activities and sorting
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = callbacks.delData.fieldNotes.activities.findIndex(
        (activity) => activity.rowId === active.id
      );
      const newIndex = callbacks.delData.fieldNotes.activities.findIndex(
        (activity) => activity.rowId === over.id
      );
      const newActivities = [...callbacks.delData.fieldNotes.activities];
      const [movedActivity] = newActivities.splice(oldIndex, 1);
      newActivities.splice(newIndex, 0, movedActivity);
      callbacks.resetNoteOrder(newActivities, callbacks.emitPatch);
    }
  };

  return (
    <div className={styles.fieldNotesActivitiesContainer}>
      <div className={styles.activitiesHeader}>
        <h3 className={styles.activitiesTitle}>Activities</h3>
      </div>
      <div className={styles.fieldNotesBubblesContainer} ref={containerRef}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={callbacks.delData.fieldNotes.activities.map((a) => a.rowId)}
            strategy={verticalListSortingStrategy}
          >
            {callbacks.delData.fieldNotes.activities.map((activity) => (
              <FieldNotesBubble
                key={activity.rowId}
                activity={activity}
                callbacks={callbacks}
                removeUnitRef={removeUnitRef}
                onEdit={onEditNote}
              />
            ))}
          </SortableContext>
        </DndContext>
        <div ref={bottomAnchorRef} />
      </div>
      <div className={styles.fieldNotesAddNoteButtonContainer}>
        <button
          onClick={handleAddNoteButtonClick}
          className={styles.fieldNotesAddNoteButton}
          disabled={callbacks.delMode === "view"}
        >
          Add Note
        </button>
      </div>
    </div>
  );
};

// Helper to get autosave field error for a specific field
const getAutosaveFieldError = (autosaveErrors, fieldName) => {
  if (!autosaveErrors?.fieldErrors) return null;
  return autosaveErrors.fieldErrors[fieldName] || null;
};

// Helper to get autosave row error for a specific activity row
const getAutosaveRowError = (autosaveErrors, rowId) => {
  if (!autosaveErrors?.rowErrors) return null;
  return autosaveErrors.rowErrors[rowId] || null;
};

// Field Notes Tab component
export const FieldNotesTab = ({ fieldNotesProps, actions }) => {
  // Local modal state - keeps modal form values local for immediate UI updates
  const [modalState, setModalState] = useState({
    isOpen: false,
    isEditing: false,
    activityRowId: null,
    time: null,
    notes: null,
  });

  const fieldNoteState = {
    // Core data
    delData: fieldNotesProps.delData,
    delMode: fieldNotesProps.delMode,
    delErrors: fieldNotesProps.delErrors,

    // UI state
    loading: fieldNotesProps.fieldNotesUI.loading,
    showAll: fieldNotesProps.showAll,
    generalWorkDescriptionWeatherCollapsed:
      fieldNotesProps.fieldNotesUI.generalWorkDescriptionWeatherCollapsed,

    // Action methods - delegate to global actions
    ...actions,

    // DEL autosave websockets - passed down from DelForm
    emitPatch: fieldNotesProps.emitPatch,

    // Autosave errors for inline error display
    autosaveErrors: fieldNotesProps.autosaveErrors,
    getAutosaveFieldError: (fieldName) =>
      getAutosaveFieldError(fieldNotesProps.autosaveErrors, fieldName),
    getAutosaveRowError: (rowId) =>
      getAutosaveRowError(fieldNotesProps.autosaveErrors, rowId),
  };

  // Putting this here to watch for changes in the remove unit button, so we don't scroll to the bottom of the table when it's clicked.
  const removeUnitRef = useRef(null);

  // Modal handlers - all local state management
  const openModalForNew = () => {
    setModalState({
      isOpen: true,
      isEditing: false,
      activityRowId: null,
      time: null,
      notes: null,
    });
  };

  const openModalForEdit = (activity) => {
    setModalState({
      isOpen: true,
      isEditing: true,
      activityRowId: activity.rowId,
      time: activity.time || null,
      notes: activity.notes || null,
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      isEditing: false,
      activityRowId: null,
      time: null,
      notes: null,
    });
  };

  const updateModalTime = (time) => {
    setModalState((prev) => ({ ...prev, time }));
  };

  const updateModalNotes = (notes) => {
    setModalState((prev) => ({ ...prev, notes }));
  };

  const clearModalFields = () => {
    setModalState((prev) => ({ ...prev, time: null, notes: null }));
  };

  const handleModalSave = () => {
    // Call the action to save to the data model and emit patch
    actions.saveActivity(
      {
        isEditing: modalState.isEditing,
        activityRowId: modalState.activityRowId,
        time: modalState.time,
        notes: modalState.notes,
      },
      fieldNotesProps.emitPatch
    );
    // Close the modal
    closeModal();
  };

  return (
    <div className={styles.fieldNotesContainer}>
      <GeneralWorkDescriptionAndWeather
        callbacks={fieldNoteState}
        removeUnitRef={removeUnitRef}
      />
      <FieldNotesActivities
        callbacks={fieldNoteState}
        removeUnitRef={removeUnitRef}
        onAddNote={openModalForNew}
        onEditNote={openModalForEdit}
      />

      {/* Field Note Modal */}
      <FieldNoteModal
        isOpen={modalState.isOpen}
        isEditing={modalState.isEditing}
        time={modalState.time}
        notes={modalState.notes}
        onTimeChange={updateModalTime}
        onNotesChange={updateModalNotes}
        onClear={clearModalFields}
        onSave={handleModalSave}
        onClose={closeModal}
      />
    </div>
  );
};
